import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/components/common';
import { referralService, ReferralStats } from '@/services/referral.service';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://www.atscvchecker.pro';

export function ReferralPage() {
  const toast = useToast();
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    referralService.getStats()
      .then(setStats)
      .catch(() => toast.error('Failed to load referral data'))
      .finally(() => setLoading(false));
  }, []);

  const referralLink = stats ? `${SITE_URL}/r/${stats.referralCode}` : '';

  const handleCopy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  }, [toast]);

  const shareText = `I use ATS CV Checker to optimize my resume for ATS systems. Check your CV score for free:`;
  const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink + '?utm_source=referral&utm_medium=linkedin&utm_campaign=friend_invite')}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink + '?utm_source=referral&utm_medium=facebook&utm_campaign=friend_invite')}&quote=${encodeURIComponent(shareText)}`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(referralLink + '?utm_source=referral&utm_medium=twitter&utm_campaign=friend_invite')}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-8 py-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Invite Friends, Get More Credits</h1>
        <p className="text-text-secondary mt-1">
          Share your referral link. When a friend signs up and runs their first ATS check,
          you both get bonus ATS checks and CV generations.
        </p>
      </div>

      {/* How it works */}
      <div className="bg-surface border border-border rounded-xl p-6">
        <h2 className="text-lg font-semibold text-text-primary mb-4">How it works</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="w-10 h-10 bg-primary/10 text-primary rounded-full flex items-center justify-center mx-auto mb-2 text-lg font-bold">1</div>
            <p className="text-sm text-text-primary font-medium">Share your link</p>
            <p className="text-xs text-text-secondary mt-1">Send to friends who are job hunting</p>
          </div>
          <div className="text-center">
            <div className="w-10 h-10 bg-primary/10 text-primary rounded-full flex items-center justify-center mx-auto mb-2 text-lg font-bold">2</div>
            <p className="text-sm text-text-primary font-medium">They sign up free</p>
            <p className="text-xs text-text-secondary mt-1">And run their first ATS analysis</p>
          </div>
          <div className="text-center">
            <div className="w-10 h-10 bg-primary/10 text-primary rounded-full flex items-center justify-center mx-auto mb-2 text-lg font-bold">3</div>
            <p className="text-sm text-text-primary font-medium">You both earn credits</p>
            <p className="text-xs text-text-secondary mt-1">+2 quick checks, +1 full analysis, +3 CV generations each</p>
          </div>
        </div>
      </div>

      {/* Referral link */}
      {stats && (
        <div className="bg-surface border border-border rounded-xl p-6">
          <h2 className="text-lg font-semibold text-text-primary mb-3">Your referral link</h2>
          <div className="flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={referralLink}
              className="flex-1 bg-background border border-border rounded-lg px-3 py-2 text-sm text-text-primary"
            />
            <button
              onClick={() => handleCopy(referralLink)}
              className="px-4 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors whitespace-nowrap min-h-[44px]"
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>

          <div className="flex items-center gap-2 mt-4 flex-wrap">
            <span className="text-sm text-text-secondary">Share on:</span>
            <a href={linkedInUrl} target="_blank" rel="noopener noreferrer"
               className="px-4 py-2.5 bg-[#0077B5] text-white rounded text-sm font-medium hover:opacity-90 min-h-[44px] inline-flex items-center">
              LinkedIn
            </a>
            <a href={facebookUrl} target="_blank" rel="noopener noreferrer"
               className="px-4 py-2.5 bg-[#1877F2] text-white rounded text-sm font-medium hover:opacity-90 min-h-[44px] inline-flex items-center">
              Facebook
            </a>
            <a href={twitterUrl} target="_blank" rel="noopener noreferrer"
               className="px-4 py-2.5 bg-[#000] text-white rounded text-sm font-medium hover:opacity-90 min-h-[44px] inline-flex items-center">
              X
            </a>
          </div>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="bg-surface border border-border rounded-xl p-6">
          <h2 className="text-lg font-semibold text-text-primary mb-4">Your stats</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-2xl font-bold text-text-primary">{stats.totalReferred}</p>
              <p className="text-xs text-text-secondary">Friends invited</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-success">{stats.completedReferrals}</p>
              <p className="text-xs text-text-secondary">Completed</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-primary">+{stats.bonusAtsQuick}</p>
              <p className="text-xs text-text-secondary">Bonus quick checks</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-primary">+{stats.bonusAtsFull}</p>
              <p className="text-xs text-text-secondary">Bonus full analyses</p>
            </div>
          </div>
          {stats.bonusAtsQuick >= stats.maxBonusAtsQuick && (
            <p className="text-xs text-text-secondary mt-3">
              You've reached the maximum referral bonus. Thank you for sharing!
            </p>
          )}
        </div>
      )}
    </div>
  );
}
